import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CloudDownload as ExternalIcon,
  Home as InternalIcon,
} from '@mui/icons-material';
import addressSearchService, { AddressSearchItem } from '../services/addressSearchService';
import { adressenApi } from '../services/api';
import type { Adresse, CreateAdresseDto } from '../types/api';

interface AddressSearchAutocompleteProps {
  /** Callback wenn eine Adresse ausgewählt wurde */
  onAddressSelected: (adresse: Adresse) => void;
  /** Optional: Callback wenn eine externe Adresse gefunden und erstellt wurde */
  onExternalAddressCreated?: (adresse: Adresse) => void;
  /** Optional: Location für externe Suche (z.B. "Zürich") */
  location?: string;
  /** Optional: Label für das Eingabefeld */
  label?: string;
  /** Optional: Placeholder */
  placeholder?: string;
  /** Optional: Vollständige Breite */
  fullWidth?: boolean;
  /** Optional: Nur interne Suche */
  internalOnly?: boolean;
  /** Optional: Nur externe Suche */
  externalOnly?: boolean;
  /** Optional: Automatisch neue Adresse erstellen bei externer Auswahl */
  autoCreateExternal?: boolean;
  /** Optional: Disabled state */
  disabled?: boolean;
}

const AddressSearchAutocomplete: React.FC<AddressSearchAutocompleteProps> = ({
  onAddressSelected,
  onExternalAddressCreated,
  location,
  label = 'Adresse suchen',
  placeholder = 'Name, Firma oder Adresse eingeben...',
  fullWidth = true,
  internalOnly = false,
  externalOnly = false,
  autoCreateExternal = true,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<AddressSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatingAddress, setCreatingAddress] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Search function
  const searchAddresses = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      if (internalOnly) {
        result = await addressSearchService.searchInternal(query, 10);
      } else if (externalOnly) {
        result = await addressSearchService.searchExternal(query, location, 10);
      } else {
        result = await addressSearchService.searchCombined(query, location, 10);
      }

      if (result.success) {
        setOptions(result.addresses);
      } else {
        setError(result.errorMessage || 'Fehler bei der Suche');
        setOptions([]);
      }
    } catch (err: any) {
      console.error('Fehler bei Adresssuche:', err);
      setError(err.message || 'Fehler bei der Suche');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [internalOnly, externalOnly, location]);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      searchAddresses(inputValue);
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, searchAddresses]);

  // Handler für Adress-Auswahl
  const handleAddressSelect = async (
    _event: React.SyntheticEvent,
    value: AddressSearchItem | null
  ) => {
    if (!value) return;

    // Wenn es eine interne Adresse ist, direkt zurückgeben
    if (value.isInternal && value.id) {
      try {
        const response = await adressenApi.getById(parseInt(value.id));
        onAddressSelected(response.data);
      } catch (err) {
        console.error('Fehler beim Laden der Adresse:', err);
        setError('Adresse konnte nicht geladen werden');
      }
      return;
    }

    // Externe Adresse: Erstellen oder Duplikat finden
    if (!value.isInternal && autoCreateExternal) {
      setCreatingAddress(true);
      setError(null);

      try {
        // Konvertiere zu CreateAdresseDto
        const dto = addressSearchService.toCreateAdresseDto(value);
        const createDto: CreateAdresseDto = {
          anrede: dto.anrede || '',
          vorname: dto.vorname || '',
          nachname: dto.nachname || '',
          firma: dto.firma,
          strasse: dto.strasse,
          hausnummer: dto.hausnummer,
          plz: dto.plz,
          ort: dto.ort,
          land: dto.land,
          telefon: dto.telefon,
          email: dto.email,
        };

        // Prüfe auf Duplikat (nach Firma + PLZ oder Name + PLZ)
        const existingAddresses = await adressenApi.getAll({
          search: value.company || value.name || '',
        });

        const duplicate = existingAddresses.data.find((addr) => {
          // Match by Firma + PLZ
          if (value.company && addr.firma) {
            return (
              addr.firma.toLowerCase() === value.company.toLowerCase() &&
              addr.plz === value.postalCode
            );
          }
          // Match by Name + PLZ
          if (value.name && (addr.vorname || addr.nachname)) {
            const fullName = `${addr.vorname || ''} ${addr.nachname || ''}`.trim().toLowerCase();
            return fullName === value.name.toLowerCase() && addr.plz === value.postalCode;
          }
          return false;
        });

        if (duplicate) {
          // Duplikat gefunden - Update mit neuen Daten
          const updateDto: CreateAdresseDto = {
            ...createDto,
            anrede: duplicate.anrede || createDto.anrede,
            vorname: duplicate.vorname || createDto.vorname,
            nachname: duplicate.nachname || createDto.nachname,
          };

          // Nur updaten wenn es Änderungen gibt
          const hasChanges =
            (createDto.telefon && createDto.telefon !== duplicate.telefon) ||
            (createDto.email && createDto.email !== duplicate.email) ||
            (createDto.strasse && createDto.strasse !== duplicate.strasse);

          if (hasChanges) {
            await adressenApi.update(duplicate.adresseID, updateDto);
            const updated = await adressenApi.getById(duplicate.adresseID);
            onAddressSelected(updated.data);
            onExternalAddressCreated?.(updated.data);
          } else {
            onAddressSelected(duplicate);
          }
        } else {
          // Neue Adresse erstellen
          const created = await adressenApi.create(createDto);
          onAddressSelected(created.data);
          onExternalAddressCreated?.(created.data);
        }
      } catch (err: any) {
        console.error('Fehler beim Erstellen der Adresse:', err);
        setError('Adresse konnte nicht erstellt werden');
      } finally {
        setCreatingAddress(false);
      }
    }

    // Input zurücksetzen
    setInputValue('');
    setOptions([]);
  };

  return (
    <Box>
      <Autocomplete
        fullWidth={fullWidth}
        disabled={disabled || creatingAddress}
        options={options}
        loading={loading || creatingAddress}
        inputValue={inputValue}
        onInputChange={(_event, newValue) => setInputValue(newValue)}
        onChange={handleAddressSelect}
        getOptionLabel={(option) => option.formattedAddress || ''}
        isOptionEqualToValue={(option, value) =>
          option.id === value.id && option.source === value.source
        }
        filterOptions={(x) => x} // Disable built-in filtering (server-side)
        noOptionsText={
          inputValue.length < 2
            ? 'Mindestens 2 Zeichen eingeben...'
            : 'Keine Adressen gefunden'
        }
        loadingText="Suche..."
        PaperComponent={({ children, ...props }) => (
          <Paper {...props} elevation={8}>
            {children}
          </Paper>
        )}
        renderOption={(props, option) => (
          <li {...props} key={`${option.source}-${option.id || option.formattedAddress}`}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
              {/* Icon */}
              <Box sx={{ pt: 0.5 }}>
                {option.company ? (
                  <BusinessIcon color="action" fontSize="small" />
                ) : (
                  <PersonIcon color="action" fontSize="small" />
                )}
              </Box>

              {/* Adress-Details */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {option.company || option.name || 'Unbekannt'}
                </Typography>
                {option.company && option.name && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.name}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {option.street && `${option.street}, `}
                  {option.postalCode} {option.city}
                </Typography>
                {(option.phone || option.email) && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {option.phone}
                    {option.phone && option.email && ' | '}
                    {option.email}
                  </Typography>
                )}
              </Box>

              {/* Source Badge */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Chip
                  size="small"
                  icon={option.isInternal ? <InternalIcon /> : <ExternalIcon />}
                  label={option.isInternal ? 'Intern' : option.source}
                  color={option.isInternal ? 'primary' : 'default'}
                  variant={option.isInternal ? 'filled' : 'outlined'}
                  sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.5 } }}
                />
                {option.confidence > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {Math.round(option.confidence * 100)}% Match
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || creatingAddress) && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            helperText={
              creatingAddress
                ? 'Adresse wird gespeichert...'
                : !internalOnly && !externalOnly
                ? 'Sucht in Skeena-Datenbank und im Internet'
                : undefined
            }
          />
        )}
      />

      {/* Trennlinie zwischen internen und externen Ergebnissen */}
      {options.length > 0 && options.some((o) => o.isInternal) && options.some((o) => !o.isInternal) && (
        <Box sx={{ display: 'none' }}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Web-Ergebnisse
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default AddressSearchAutocomplete;
